"use client";

import { useState, useEffect } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useTranslations } from "@/contexts/I18nContext";

interface Slide {
  emoji: string;
  titleKey: string;
  bodyKey: string;
}

const SLIDES: Slide[] = [
  { emoji: "🏠", titleKey: "welcomeOverlay.slide1.title", bodyKey: "welcomeOverlay.slide1.body" },
  { emoji: "✅", titleKey: "welcomeOverlay.slide2.title", bodyKey: "welcomeOverlay.slide2.body" },
  { emoji: "💬", titleKey: "welcomeOverlay.slide3.title", bodyKey: "welcomeOverlay.slide3.body" },
  { emoji: "✨", titleKey: "welcomeOverlay.slide4.title", bodyKey: "welcomeOverlay.slide4.body" },
];

interface WelcomeOverlayProps {
  onDismiss?: () => void;
}

/**
 * 4-slide post-registration carousel mirroring iOS WelcomeOverlayView and
 * Android WelcomeOverlay. Persists `welcome_overlay_shown: true` on
 * member_profiles when dismissed.
 */
export function WelcomeOverlay({ onDismiss }: WelcomeOverlayProps) {
  const { profile } = useHouseholdContext();
  const t = useTranslations();
  const [index, setIndex] = useState(0);
  const [dismissing, setDismissing] = useState(false);

  // Auto-advance every 4s. Loops back to first slide.
  useEffect(() => {
    const id = setTimeout(() => {
      setIndex((i) => (i + 1) % SLIDES.length);
    }, 4000);
    return () => clearTimeout(id);
  }, [index]);

  const dismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    if (profile?.uid) {
      try {
        await updateDoc(doc(db, "member_profiles", profile.uid), {
          welcome_overlay_shown: true,
        });
      } catch (err) {
        console.error("Failed to mark welcome_overlay_shown:", err);
      }
    }
    onDismiss?.();
  };

  const slide = SLIDES[index];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-3xl shadow-2xl overflow-hidden">
        <div className="p-8 text-center space-y-4">
          <div className="text-6xl" aria-hidden="true">
            {slide.emoji}
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 whitespace-pre-line">
            {t(slide.titleKey)}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            {t(slide.bodyKey)}
          </p>
        </div>

        {/* Pager dots */}
        <div className="flex items-center justify-center gap-2 pb-4">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={t("welcomeOverlay.slideAria", { n: i + 1 })}
              className={`h-2 rounded-full transition-all ${
                i === index
                  ? "w-6 bg-[color:var(--accent-color)]"
                  : "w-2 bg-gray-300 dark:bg-gray-700"
              }`}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="flex gap-2 px-6 pb-6">
          <button
            type="button"
            onClick={dismiss}
            className="flex-1 py-2.5 rounded-lg text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
          >
            {t("common.skip")}
          </button>
          <button
            type="button"
            onClick={() => {
              if (index < SLIDES.length - 1) {
                setIndex(index + 1);
              } else {
                void dismiss();
              }
            }}
            className="flex-1 py-2.5 rounded-lg text-sm font-semibold text-white bg-[color:var(--accent-color)]"
          >
            {index < SLIDES.length - 1 ? t("common.next") : t("welcomeOverlay.getStarted")}
          </button>
        </div>
      </div>
    </div>
  );
}
